import {
  type CareerHistoryFormValues,
  normaliseCareerHistory,
} from '@seek/ca-myprofile-utils';
import { CareerHistoryForm } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Stack, Text } from 'braid-design-system';

import type { Role } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useCareerHistoryMutations } from './hooks/useCareerHistoryMutations';
import { useEditorTracking } from './hooks/useTracking';

export function CareerHistoryEditor({
  roles,
  unconfirmedRoles,
  shouldCloseDrawerOnExit = false,
}: {
  roles: Role[];
  unconfirmedRoles: Role[];
  shouldCloseDrawerOnExit?: boolean;
}) {
  const { t } = useTranslations(translations);
  const { config } = useConfig();
  const { editingId, onEditorClose, onFormDirty, switchToListView } =
    useEditorDrawer();
  const onExit = shouldCloseDrawerOnExit ? onEditorClose : switchToListView;

  const { trackAddRole, trackEditRole, trackDeleteRole, trackRejectRole } =
    useEditorTracking();
  const { error, confirmRole, updateRole, deleteRole, rejectRole } =
    useCareerHistoryMutations();

  const handleSubmit = async (values: CareerHistoryFormValues) => {
    try {
      const roleValues = normaliseCareerHistory(values);
      // confirmEdited find id in unconfirmed roles
      // then use confirmRole to update the role
      const found = unconfirmedRoles.find((r) => r.id === roleValues.id);

      if (found) {
        await confirmRole(roleValues);

        trackAddRole(found, 'cv parsed');
      } else {
        await updateRole(roleValues.id, roleValues);

        const isNewRole = !roles.find((r) => r.id === roleValues.id);

        if (isNewRole) {
          trackAddRole(roleValues);
        } else {
          trackEditRole(roleValues);
        }
      }

      onExit();
    } catch (err: any) {
      logError({ ...err, type: 'update-role' }, { error: err });
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;

    try {
      const found = unconfirmedRoles.find((r) => r.id === editingId);

      if (found) {
        await rejectRole(editingId);

        trackRejectRole();
      } else {
        const role = roles.find((r) => r.id === editingId);

        if (role) {
          await deleteRole(editingId);

          trackDeleteRole();
        }
      }

      onExit();
    } catch (err: any) {
      logError({ ...err, type: 'delete-role' }, { error: err });
    }
  };

  const allRoles = [...unconfirmedRoles, ...roles];
  const defaultValues = allRoles.find((role) => role.id === editingId);
  const isDeletable = editingId && defaultValues && allRoles.length > 1;

  if (!editingId && !shouldCloseDrawerOnExit) return null;

  return (
    <Stack space="small">
      <CareerHistoryForm
        zone={config.ZONE}
        // @ts-expect-error title.ontologyId mistmatch
        initialValues={defaultValues}
        onSubmit={({ values }) => handleSubmit(values)}
        onCancel={onExit}
        onDelete={isDeletable ? handleDelete : undefined}
        onFormDirtyChange={onFormDirty}
        scrollToTopOnMobile={false}
      />
      {error && (
        <Alert tone="critical" data={{ automation: 'career-history-error' }}>
          <Text>{t('Your career history could not be saved')}</Text>
        </Alert>
      )}
    </Stack>
  );
}
